import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation Cover
    FaTools,            // For Engine Protection Cover
    FaTruckPickup,      // For Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaUserShield,       // For Personal Accident Cover
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
    FaCarSide,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const TVSTwoWheelerInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is insurance mandatory for my TVS scooter?",
      answer: "Yes, at least third-party insurance is legally required to ride on Indian roads.",
    },
    {
      question: "Which TVS model has the lowest insurance cost?",
      answer: "TVS Scooty Pep+ and Sport generally have the lowest premiums due to lower IDV.",
    },
    {
      question: "Can I buy insurance online for my Apache or Jupiter?",
      answer: "Yes, all TVS models can be insured online with most insurers offering digital KYC and instant policy issuance.",
    },
    {
      question: "What is zero depreciation and should I opt for it?",
      answer: "It ensures full claim value for parts replaced, highly recommended for new TVS models or if you ride frequently.",
    },
    {
      question: "Does comprehensive insurance cover engine damage?",
      answer: "Not always. You need to opt for an engine protection add-on.",
    },
    {
      question: "Is roadside assistance useful for city riders?",
      answer: "Yes, especially for scooters like Jupiter or Ntorq which are used daily.",
    },
    {
      question: "Can I retain my NCB if I sell my old TVS and buy a new one?",
      answer: "Yes, NCB is transferable if the new bike is under your name.",
    },
    {
      question: "How to renew TVS insurance after expiry?",
      answer: "Insurers may inspect your vehicle. It’s best to renew on time to avoid a lapse and maintain NCB.",
    },
    {
      question: "Will a modified TVS bike be insured?",
      answer: "Only if modifications are disclosed and approved by the insurer.",
    },
    {
      question: "What happens if I ride without insurance?",
      answer: "You may face fines up to ₹2,000 or more under the Motor Vehicles Act.",
    },
    {
      question: "Are EVs like TVS iQube covered under the same policy?",
      answer: "No, EV-specific policies are issued, but the process remains similar.",
    },
    {
      question: "Is cashless repair available at all TVS service centers?",
      answer: "Only at insurer-authorized garages. Many large insurers have tie-ups with TVS.",
    },
    {
      question: "Can I insure an old TVS bike that’s more than 10 years old?",
      answer: "Yes, though IDV will be lower and only a few insurers might offer full comprehensive coverage.",
    },
    {
      question: "Are damages from road rage or riots covered?",
      answer: "Usually yes, under comprehensive plans unless explicitly excluded.",
    },
    {
      question: "Can I claim insurance if my TVS is stolen?",
      answer: "Yes, file an FIR and submit all required documents to initiate a theft claim.",
    },
    {
      question: "Do I need a separate pillion cover?",
      answer: "Some insurers offer pillion rider covers for an extra premium.",
    },
    {
      question: "Is it better to buy from the dealership or online?",
      answer: "Online plans are usually cheaper and allow better comparison of features.",
    },
    {
      question: "Are engine and gear box repairs included?",
      answer: "Only with an engine protection add-on, not under basic plans.",
    },
    {
      question: "Can I insure accessories installed on my TVS?",
      answer: "Yes, if declared and valued at the time of buying the policy.",
    },
    {
      question: "Does theFinMaster help compare insurance for TVS?",
      answer: "Yes, you can explore, compare and buy plans for TVS bikes and scooters directly on the platform.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "Ideal for premium models like Apache and Ronin, this ensures you get the full claim without depreciation deductions.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection",
      description: "Helpful during monsoon for scooters like Jupiter and Ntorq that are often used in cities prone to waterlogging.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Recommended for long-distance riders or commuters facing frequent breakdowns.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Personal Accident Cover",
      description: "Mandatory in most cases, but essential for financial safety.",
      icon: <FaUserShield className="text-4xl text-purple-600 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Pays for parts like oil, screws, and brake fluid usually excluded in standard claims.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];
const tvsinsurance = [
    {
      name: "Jupiter",
      path: "/tvs-jupiter-bike-insurance", 
      image: "/assets/tvsjupiter.jpeg",
    },
 {
      name: "Raider 125",
      path: "/tvs-raider125-bike-insurance", 
      image: "/assets/tvsraider125.jpeg",
    },
    {
      name: "Apache RTR1604V",
      path: "/tvs-apache-rtr1604v-bike-insurance", 
      image: "/assets/tvsapachertr1604v.jpeg",
    },
    {
      name: "I Qube",
      path: "/tvs-iqube-bike-insurance", 
      image: "/assets/tvsiqube.jpeg",
    },
     {
      name: "Ntorq 125",
      path: "/tvs-ntorq125-bike-insurance", 
      image: "/assets/tvsntorq125.jpeg",
    },
     {
      name: "Sport",
      path: "/tvs-sport-bike-insurance", 
      image: "/assets/tvssport.jpeg",
    },
    {
      name: "Radeon",
      path: "/tvs-radeon-bike-insurance", 
      image: "/assets/tvsradeon.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>TVS Two-Wheeler Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore TVS two-wheeler insurance options in India. Compare quotes, customize coverage, and buy online to protect your TVS bike or scooter."
        />
        <meta
          name="keywords"
          content="tvs two-wheeler insurance, bike insurance india, tvs jupiter insurance, tvs apache insurance, tvs ntorq insurance, two-wheeler insurance quotes"
        />
            <link rel="canonical" href="https://thefinmaster.com/tvs-two-wheeler-insurance" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/tvs-two-wheeler-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="TVS Two-Wheeler Insurance in India" />
        <meta property="og:description" content="Compare and buy TVS two-wheeler insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/tvs-two-wheeler-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">TVS Two-Wheeler Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">TVS Two-Wheeler Insurance </span>  
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                TVS is celebrated for its innovation, performance, and wide range of two-wheelers catering to every rider. Whether you own a practical Star City or a sporty Apache, securing your bike with the right insurance is essential. This guide walks you through everything you need to know about insuring a TVS bike, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/tvsinsurance.jpeg"
                alt="TVS Two-Wheeler"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About TVS in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            TVS has built a strong reputation in India over the decades with its versatile and performance-driven lineup. It caters to both commuter needs and performance enthusiasts with scooters like Jupiter and Scooty Pep+, and motorcycles such as Apache RTR, Raider, and Sport.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            TVS stands out for its innovation, especially in scooters and performance bikes, making it a favorite among college-goers, city commuters, and rural users alike. Ensuring your vehicle is properly insured is essential for staying legally compliant and financially protected.
          </p>
        </div>
      </div>

       <div className="p-4 sm:p-8 bg-white">
                                                              <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                                                Choose Your Bike Brand
                                                              </h2>
                                                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                                                {tvsinsurance.map((calc, index) => (
                                                                  <motion.div
                                                                    key={index}
                                                                    whileHover={{ scale: 1.04 }}
                                                                    initial={{ opacity: 0, y: 20 }}
                                                                    animate={{ opacity: 1, y: 0 }}
                                                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                                                  >
                                                                    <Link
                                                                      to={calc.path}
                                                                      className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                                                    >
                                                                      <img
                                                                        src={calc.image}
                                                                        alt={calc.name}
                                                                        className="w-16 h-16 object-contain mb-2"
                                                                      />
                                                                      <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                                                        {calc.name}
                                                                      </span>
                                                                    </Link>
                                                                  </motion.div>
                                                                ))}
                                                              </div>
                                                            </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why TVS Two-Wheelers Need Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            TVS owners need insurance for various risks they may face on the road or while parked:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Road accidents or collisions</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft or vandalism</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Fire, floods, or natural disasters</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Liability to third parties</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Damage to critical components like engine, brakes, or wheels</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive two-wheeler insurance policy provides financial coverage and access to cashless garages for quick and quality repairs.
          </div>
        </motion.div>
      </section>
          <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
             <div className="flex flex-col md:flex-row justify-between items-center gap-10">
               <div className="max-w-xl space-y-6 text-center md:text-left">
                 <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
                   Calculate Your TwoWheeler Insurance <br />
                   in <span className="text-blue-600">Seconds</span>
                 </h2>
                 <p className="text-gray-700 text-lg">
                   Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
                 </p>
                 <Link
                   to="/financial-tools-calculators/bike-insurance-calculator"
                   className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
                 >
                   Start Calculating <ArrowRight className="w-5 h-5" />
                 </Link>
               </div>
               <div className="space-y-4 text-left">
                 {features.map((item, index) => (
                   <div key={index} className="flex items-center gap-3">
                     <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                     <span className="text-gray-800 font-medium">{item.text}</span>
                   </div>
                 ))}
               </div>
             </div>
           </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular TVS Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here's an estimate of the most popular TVS models, their insured declared value (IDV), and expected insurance premiums:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "TVS Jupiter", category: "Commuter Scooter", idv: "70,000 – 90,000", premium: "₹850 – ₹1,300" },
              { model: "TVS Ntorq 125", category: "Performance Scooter", idv: "85,000 – 1.1 Lakh", premium: "₹1,000 – ₹1,500" },
              { model: "TVS Scooty Pep+", category: "Lightweight Scooter", idv: "60,000 – 75,000", premium: "₹750 – ₹1,100" },
              { model: "TVS Apache RTR Series", category: "Sport Commuter Bike", idv: "1 – 1.5 Lakh", premium: "₹1,200 – ₹1,800" },
              { model: "TVS Raider 125", category: "Urban Commuter Bike", idv: "85,000 – 1.1 Lakh", premium: "₹950 – ₹1,400" },
              { model: "TVS Sport", category: "Budget Commuter Bike", idv: "60,000 – 80,000", premium: "₹800 – ₹1,100" },
              { model: "TVS Ronin", category: "Lifestyle Cruiser Bike", idv: "1.4 – 1.6 Lakh", premium: "₹1,300 – ₹1,900" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">TVS Model</th>
                  <th className="p-3 border border-gray-300 text-left">Type</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["TVS Jupiter", "Commuter Scooter", "70,000 – 90,000", "₹850 – ₹1,300"],
                  ["TVS Ntorq 125", "Performance Scooter", "85,000 – 1.1 Lakh", "₹1,000 – ₹1,500"],
                  ["TVS Scooty Pep+", "Lightweight Scooter", "60,000 – 75,000", "₹750 – ₹1,100"],
                  ["TVS Apache RTR Series", "Sport Commuter Bike", "1 – 1.5 Lakh", "₹1,200 – ₹1,800"],
                  ["TVS Raider 125", "Urban Commuter Bike", "85,000 – 1.1 Lakh", "₹950 – ₹1,400"],
                  ["TVS Sport", "Budget Commuter Bike", "60,000 – 80,000", "₹800 – ₹1,100"],
                  ["TVS Ronin", "Lifestyle Cruiser Bike", "1.4 – 1.6 Lakh", "₹1,300 – ₹1,900"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons Recommended for TVS Bikes and Scooters
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Consider these useful add-ons for better protection:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurers for TVS Two-Wheeler Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Leading companies offering TVS insurance with excellent service and claim processes:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">HDFC ERGO</h3>
            <p className="text-gray-700 text-sm mt-1">Strong network garage coverage, fast digital claim filing.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Digit Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Ideal for younger riders or scooter users due to simple mobile-first policies.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ICICI Lombard</h3>
            <p className="text-gray-700 text-sm mt-1">Offers great plans for high-value TVS models like Apache.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">SBI General Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Affordable premiums and wide branch presence.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Tata AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Strong add-on options with easy online claim assistance.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Save on TVS Two-Wheeler Insurance Premium
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Lower your insurance cost with these tips:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Avoid small claims and build No Claim Bonus (NCB) year on year.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Use voluntary deductibles wisely to reduce base premium.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Fit anti-theft devices approved by ARAI to qualify for discounts.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare quotes across insurers before purchasing or renewing.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Select only necessary add-ons based on usage (city vs highway vs occasional).</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for TVS Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To buy or renew your policy, keep these documents handy:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>TVS bike or scooter registration certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous year’s insurance details (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Proof of ID and address (e.g., Aadhaar, PAN)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Passport-size photo (if required)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          TVS Two-Wheeler Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                TVS bikes and scooters offer great reliability, performance, and value. Whether you ride a Jupiter, Ntorq, Apache or Scooty Pep+, securing the right insurance keeps you protected from financial shocks, legal trouble, and repair hassles.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                TheFinMaster simplifies the process by helping you compare insurers, select ideal add-ons, and purchase a plan that suits your budget and riding style.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default TVSTwoWheelerInsurance;